/**
 * O botão flutuante de feedback, em todas as telas (#561).
 *
 * Quem usa o sistema escreve o que achou, com prints se quiser, e envia ao
 * suporte da Cliqfy. A tela, a pessoa e a hora vão sozinhas: aparecem no
 * diálogo para a pessoa saber o que acompanha o texto, mas não são campos.
 *
 * ── Ao lado do balão de suporte, e não em cima ────────────────────────────
 *
 * O balão do chat de suporte ocupa o canto inferior direito. O botão de
 * feedback fica logo acima dele, para os dois não disputarem o mesmo lugar.
 *
 * ── As imagens chegam no tamanho que a API aceita, e sem metadados ────────
 *
 * Toda imagem é redesenhada antes de ir (`preparar`): isso tira o GPS e o
 * aparelho de uma foto de celular, e reduz em passos o que passa dos 2 MB.
 */

import { useEffect, useRef, useState, type ClipboardEvent, type ReactNode } from 'react';
import { LIMITES_DO_FEEDBACK } from '@cliqcrm/contracts';
import { Botao, CampoArea, Dialogo, Icone } from '@/shared/ui';
import { useApi } from '@/shared/api/consultas';
import { recadoDeErro } from '@/shared/api/recado-de-erro';
import { Faixa } from '@/shared/ui/estados/faixa';
import './feedback.css';

const BYTES_MAXIMOS = LIMITES_DO_FEEDBACK.megabytesPorImagem * 1024 * 1024;
const LADO_MAXIMO = 2560;

export interface TelaDoFeedback {
  /** Identifica a tela: área do menu e, em Configurações, a seção. */
  readonly caminho: string;
  /** Como a tela aparece no menu: "Funil", "Configurações › Conexões". */
  readonly nome: string;
}

interface ImagemAnexada {
  readonly id: string;
  readonly arquivo: Blob;
  readonly previa: string;
}

/** A imagem não coube no limite nem reduzida: a tela diz qual. */
class ImagemGrandeDemais extends Error {}

function paraBlob(tela: HTMLCanvasElement, tipo: string, qualidade?: number): Promise<Blob> {
  return new Promise((resolver, rejeitar) =>
    tela.toBlob(
      (blob) =>
        blob ? resolver(blob) : rejeitar(new Error('Não foi possível preparar a imagem.')),
      tipo,
      qualidade,
    ),
  );
}

/**
 * Redesenha a imagem antes de mandar — SEMPRE, mesmo a que já cabe.
 *
 * Redesenhar descarta os metadados da foto (GPS, aparelho, data): uma foto de
 * celular levaria a localização junto, e nada no print precisa dela. O PNG
 * continua PNG quando cabe, para o texto do print não borrar; o que não cabe
 * vira JPEG, reduzido em passos até caber em 2 MB.
 */
async function preparar(arquivo: File): Promise<Blob> {
  const imagem = await createImageBitmap(arquivo);
  const tela = document.createElement('canvas');
  const contexto = tela.getContext('2d');
  if (!contexto) throw new Error('O navegador não conseguiu preparar a imagem.');
  const desenhar = (lado: number): void => {
    const escala = Math.min(1, lado / Math.max(imagem.width, imagem.height));
    tela.width = Math.round(imagem.width * escala);
    tela.height = Math.round(imagem.height * escala);
    contexto.drawImage(imagem, 0, 0, tela.width, tela.height);
  };
  try {
    if (arquivo.type === 'image/png') {
      desenhar(LADO_MAXIMO);
      const png = await paraBlob(tela, 'image/png');
      if (png.size <= BYTES_MAXIMOS) return png;
    }
    for (const lado of [LADO_MAXIMO, 1920, 1280]) {
      desenhar(lado);
      // JPEG não tem transparência: sem fundo branco, o fundo do PNG sai preto.
      contexto.globalCompositeOperation = 'destination-over';
      contexto.fillStyle = '#ffffff';
      contexto.fillRect(0, 0, tela.width, tela.height);
      contexto.globalCompositeOperation = 'source-over';
      for (const qualidade of [0.85, 0.7, 0.55]) {
        const jpeg = await paraBlob(tela, 'image/jpeg', qualidade);
        if (jpeg.size <= BYTES_MAXIMOS) return jpeg;
      }
    }
    throw new ImagemGrandeDemais(arquivo.name);
  } finally {
    imagem.close();
  }
}

const dataEHora = (instante: Date): string =>
  instante.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });

export function BotaoDeFeedback({
  tela,
  pessoa,
  recolhido = false,
}: {
  tela: TelaDoFeedback;
  pessoa: { readonly nome: string; readonly email: string };
  /** O menu recolhido: o nome sai da tela e vira a dica do navegador. */
  recolhido?: boolean;
}): ReactNode {
  const [aberto, definirAberto] = useState(false);
  const [enviado, definirEnviado] = useState(false);

  useEffect(() => {
    if (!enviado) return;
    const relogio = setTimeout(() => definirEnviado(false), 4000);
    return () => clearTimeout(relogio);
  }, [enviado]);

  return (
    <>
      {/*
        Um item do menu lateral, o último, acima de quem está logado (v3,
        #677). Era um botão flutuante no canto da tela, que cobria o conteúdo
        e não parecia do produto.
      */}
      <button
        type="button"
        className="casca__item"
        aria-haspopup="dialog"
        aria-expanded={aberto}
        onClick={() => definirAberto(true)}
        title={recolhido ? 'Feedback' : 'Mandar um feedback sobre esta tela'}
      >
        <span className="casca__icone">
          <Icone nome="nota" />
        </span>
        <span className="casca__nome">Feedback</span>
      </button>

      {aberto && (
        <DialogoDeFeedback
          tela={tela}
          pessoa={pessoa}
          aoFechar={() => definirAberto(false)}
          aoEnviar={() => {
            definirAberto(false);
            definirEnviado(true);
          }}
        />
      )}

      {enviado && (
        <div className="feedback-recado" role="status">
          Feedback enviado. Obrigado!
        </div>
      )}
    </>
  );
}

function DialogoDeFeedback({
  tela,
  pessoa,
  aoFechar,
  aoEnviar,
}: {
  tela: TelaDoFeedback;
  pessoa: { readonly nome: string; readonly email: string };
  aoFechar: () => void;
  aoEnviar: () => void;
}): ReactNode {
  const api = useApi();
  const seletor = useRef<HTMLInputElement>(null);
  // A hora do momento em que a pessoa abriu: é a que ela vê, e a que a API grava
  // fica a segundos dela.
  const [abertoEm] = useState(() => new Date());
  const [texto, definirTexto] = useState('');
  const [imagens, definirImagens] = useState<readonly ImagemAnexada[]>([]);
  const [enviando, definirEnviando] = useState(false);
  const [falha, definirFalha] = useState<string | null>(null);
  // Imagens ainda sendo preparadas: enviar agora as deixaria de fora.
  const [preparando, definirPreparando] = useState(0);

  // As prévias são URLs do navegador: liberar ao sair, senão a memória cresce.
  const previas = useRef(imagens);
  previas.current = imagens;
  // Imagem que termina de ser preparada depois de o diálogo fechar é descartada.
  const montado = useRef(true);
  // Quantas imagens já estão no feedback, atualizado sem esperar o desenho.
  const reservadas = useRef(0);
  useEffect(() => {
    montado.current = true;
    return () => {
      montado.current = false;
      previas.current.forEach((i) => URL.revokeObjectURL(i.previa));
    };
  }, []);

  async function anexar(arquivos: readonly File[]): Promise<void> {
    // Durante o envio o formulário já foi montado: o que entrasse agora sumiria.
    if (enviando) return;
    const imagensSoltas = arquivos.filter((a) => a.type.startsWith('image/'));
    const recados: string[] = [];

    /*
     * A vaga é reservada ANTES de preparar, contra um contador que muda na
     * hora: dois Ctrl+V seguidos preparam ao mesmo tempo, e a conta feita
     * depois de esperar deixaria passar seis. Cortar antes também evita
     * decodificar trinta fotos para aproveitar cinco.
     */
    const cabem = Math.max(0, LIMITES_DO_FEEDBACK.imagens - reservadas.current);
    const escolhidas = imagensSoltas.slice(0, cabem);
    if (imagensSoltas.length > cabem) {
      recados.push(`Cabem até ${LIMITES_DO_FEEDBACK.imagens} imagens por feedback.`);
    }
    reservadas.current += escolhidas.length;
    definirFalha(recados.length > 0 ? recados.join(' ') : null);
    if (escolhidas.length === 0) return;

    definirPreparando((atual) => atual + escolhidas.length);
    const preparadas = await Promise.allSettled(escolhidas.map(preparar));
    if (!montado.current) return;
    definirPreparando((atual) => atual - escolhidas.length);

    const novas: ImagemAnexada[] = [];
    preparadas.forEach((resultado, indice) => {
      if (resultado.status === 'fulfilled') {
        novas.push({
          id: `${Date.now()}-${indice}-${Math.random()}`,
          arquivo: resultado.value,
          previa: URL.createObjectURL(resultado.value),
        });
        return;
      }
      // A vaga reservada volta: a imagem não entrou.
      reservadas.current -= 1;
      recados.push(
        resultado.reason instanceof ImagemGrandeDemais
          ? `"${escolhidas[indice]!.name}" é grande demais, mesmo reduzida.`
          : `Não foi possível abrir "${escolhidas[indice]!.name}".`,
      );
    });
    definirImagens((atuais) => [...atuais, ...novas]);
    if (recados.length > 0) definirFalha(recados.join(' '));
  }

  // Fora do atualizador do estado: no modo estrito o React o chama duas vezes,
  // e o contador seria descontado duas vezes.
  function remover(id: string): void {
    const alvo = imagens.find((i) => i.id === id);
    if (!alvo) return;
    URL.revokeObjectURL(alvo.previa);
    reservadas.current -= 1;
    definirImagens((atuais) => atuais.filter((i) => i.id !== id));
  }

  // Colar um print com Ctrl+V no campo de texto anexa a imagem.
  function aoColar(evento: ClipboardEvent<HTMLTextAreaElement>): void {
    const coladas = [...evento.clipboardData.files].filter((f) => f.type.startsWith('image/'));
    if (coladas.length === 0) return;
    evento.preventDefault();
    void anexar(coladas);
  }

  async function enviar(): Promise<void> {
    if (!api || texto.trim() === '' || enviando || preparando > 0) return;
    definirEnviando(true);
    definirFalha(null);
    const formulario = new FormData();
    formulario.append('texto', texto.trim());
    formulario.append('tela_caminho', tela.caminho);
    formulario.append('tela_nome', tela.nome);
    imagens.forEach((imagem, indice) =>
      formulario.append('imagens', imagem.arquivo, `imagem-${indice + 1}`),
    );
    try {
      await api.enviarFeedback({ corpo: formulario });
      aoEnviar();
    } catch (erro) {
      definirFalha(
        recadoDeErro(erro, { generica: 'Não foi possível enviar agora. Tente de novo.' }),
      );
      definirEnviando(false);
    }
  }

  return (
    <Dialogo
      titulo="Mandar feedback"
      forma="lateral"
      aoFechar={() => {
        if (!enviando) aoFechar();
      }}
      botoes={
        <>
          <Botao variante="fantasma" desabilitado={enviando} motivo="Enviando…" onClick={aoFechar}>
            Cancelar
          </Botao>
          <Botao
            desabilitado={enviando || texto.trim() === '' || preparando > 0}
            motivo={
              enviando
                ? 'Enviando…'
                : preparando > 0
                  ? 'Esperando as imagens ficarem prontas'
                  : 'Escreva o feedback antes de enviar'
            }
            onClick={() => void enviar()}
          >
            {enviando ? 'Enviando…' : 'Enviar'}
          </Botao>
        </>
      }
    >
      <p className="dialogo__apoio">
        Conte o que achou, o que deu errado ou o que poderia melhorar. Vai direto para o suporte da
        Cliqfy.
      </p>
      {/*
        Minimização na origem (decisão de 01/10/2026): o feedback vai para fora
        da organização, e o print de uma conversa levaria o cliente junto.
      */}
      <p className="feedback__aviso">
        Não inclua dados de clientes, como nome, telefone ou documento, no texto nem nas imagens.
      </p>

      <dl className="feedback__contexto">
        <div>
          <dt>Tela</dt>
          <dd>{tela.nome}</dd>
        </div>
        <div>
          <dt>Enviado por</dt>
          <dd>{pessoa.nome}</dd>
        </div>
        <div>
          <dt>Data e hora</dt>
          <dd>{dataEHora(abertoEm)}</dd>
        </div>
      </dl>

      <CampoArea
        rotulo="Seu feedback"
        value={texto}
        rows={6}
        maxLength={LIMITES_DO_FEEDBACK.caracteres}
        autoFocus
        onChange={(evento) => definirTexto(evento.target.value)}
        onPaste={aoColar}
        placeholder="Escreva aqui. Para mandar um print, cole com Ctrl+V."
      />

      <div className="feedback__imagens">
        <input
          ref={seletor}
          type="file"
          accept="image/*"
          multiple
          hidden
          onChange={(evento) => {
            void anexar([...(evento.target.files ?? [])]);
            evento.target.value = '';
          }}
        />
        <Botao
          variante="fantasma"
          desabilitado={imagens.length >= LIMITES_DO_FEEDBACK.imagens || enviando}
          motivo={`Já são ${LIMITES_DO_FEEDBACK.imagens} imagens`}
          onClick={() => seletor.current?.click()}
        >
          Anexar imagens
        </Botao>
        <span className="feedback__limite">
          Até {LIMITES_DO_FEEDBACK.imagens} imagens ({imagens.length} anexada
          {imagens.length === 1 ? '' : 's'})
        </span>
      </div>

      {imagens.length > 0 && (
        <ul className="feedback__previas" aria-label="Imagens anexadas">
          {imagens.map((imagem, indice) => (
            <li key={imagem.id} className="feedback__previa">
              <img src={imagem.previa} alt={`Imagem ${indice + 1}`} />
              <button
                type="button"
                className="feedback__remover"
                aria-label={`Remover a imagem ${indice + 1}`}
                title="Remover"
                disabled={enviando}
                onClick={() => remover(imagem.id)}
              >
                <Icone nome="fechar" />
              </button>
            </li>
          ))}
        </ul>
      )}

      {falha && <Faixa tom="bloqueado">{falha}</Faixa>}
    </Dialogo>
  );
}
